import { AuxRadioGroup } from "@blk/aladdin-react-components-es";
import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import "components/time-period/insSettingsTimePeriod.scss";
import { DataGranularityTooltipWidget } from "components/time-period/insTooltipComponents";
import { useGetUsername } from "components/frame/hooks/use-user-preferences";
import { useWidgetCurrentGranularity, useWidgetCurrentGranularityTitle, useWidgetCurrentGranularityType, useIsWidgetLevelTimePeriod } from "components/widgets/hooks/use-current-widget";
import { useGetEntityInfo } from "components/workspaces/hooks/use-get-dashboards-by-type";
import { appInsightEvents, language } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { setCurrentStatusDataGranularity, setWidgetIsWidgetLevelTimePeriod } from "store/current-dashboard";
import { DataGranularity, DataGranularityType } from "store/current-dashboard/types";
import { getCurrentLocalTime } from "common/date-utils";
const { granularity, period, quarterly, yearly, yearOverYear, ytd } = language.en.skeleton.settingsMenu.granularity;
const dataRadioGranularity = (sectionId, widgetId) => [
    { disabled: false, eventData: DataGranularity.quarterly, label: quarterly, uid: `${sectionId}-${widgetId}-${DataGranularity.quarterly}` },
    { disabled: false, eventData: DataGranularity.yearly, label: yearly, uid: `${sectionId}-${widgetId}-${DataGranularity.yearly}` },
    { disabled: false, eventData: DataGranularity.yearOverYear, label: yearOverYear, uid: `${sectionId}-${widgetId}-${DataGranularity.yearOverYear}` }
];
const dataRadioVariation = (sectionId, widgetId, isTitleExist) => [
    { disabled: false, eventData: DataGranularity.quarterly, label: quarterly, uid: `${sectionId}-${widgetId}-${DataGranularity.quarterly}` },
    { disabled: false,
        eventData: DataGranularity.yearly,
        label: isTitleExist
            ? yearly
            : ytd,
        uid: `${sectionId}-${widgetId}-${DataGranularity.yearly}` },
    { disabled: false, eventData: DataGranularity.yearOverYear, label: yearOverYear, uid: `${sectionId}-${widgetId}-${DataGranularity.yearOverYear}` }
];
const trackGranularityChange = (granularityType, selectedLabel, entityName, entityType, userName) => {
    appInsightsTrackEvent({
        name: granularityType === DataGranularityType.Variation
            ? appInsightEvents.customEvents.names.ChangeWidgetSettingsVariationPeriod
            : appInsightEvents.customEvents.names.ChangeWidgetSettingsDataGranularity,
        properties: { LocalTimestamp: getCurrentLocalTime() }
    }, { entityName: String(entityName ?? ""), entityType: String(entityType ?? ""), selectedGranularity: selectedLabel, userName: String(userName ?? "") });
};
const prepareGranularityData = (granularityType, sectionId, widgetId, granularityTitle, currentGranularity) => {
    const data = granularityType === DataGranularityType.Variation
        ? dataRadioVariation(sectionId, widgetId, granularityTitle)
        : dataRadioGranularity(sectionId, widgetId);
    data.forEach((e) => {
        e.checked = e.eventData === currentGranularity;
    });
    return data;
};
const getGranularityLabel = (granularityTitle, granularityType) => {
    if (granularityTitle) {
        return granularityTitle;
    }
    return granularityType === DataGranularityType.Variation ? period : granularity;
};
export const InsSettingsGranularity = ({ widgetId, sectionId }) => {
    const dispatch = useDispatch();
    const { name: entityName, type: entityType } = useGetEntityInfo();
    const userName = useGetUsername();
    const widgetGranularity = useWidgetCurrentGranularity(sectionId, widgetId);
    const isWidgetLevel = useIsWidgetLevelTimePeriod(sectionId, widgetId);
    const workspaceGranularity = useSelector((state) => state.currentDashboard?.workspaceSettings?.dataGranularity);
    const currentGranularity = isWidgetLevel ? widgetGranularity : workspaceGranularity ?? widgetGranularity;
    const granularityTitle = useWidgetCurrentGranularityTitle(sectionId, widgetId);
    const granularityType = useWidgetCurrentGranularityType(sectionId, widgetId);
    const onRadioGroupChanged = useCallback((event) => {
        trackGranularityChange(granularityType, event.detail.value.label ?? "", entityName, entityType, userName);
        dispatch(setCurrentStatusDataGranularity({ granularity: event.detail.value.eventData, sectionId, widgetId }));
        dispatch(setWidgetIsWidgetLevelTimePeriod({ isWidgetLevel: true, sectionId, widgetId }));
    }, [dispatch, entityName, entityType, granularityType, sectionId, userName, widgetId]);
    const data = useMemo(() => prepareGranularityData(granularityType, sectionId, widgetId, granularityTitle, currentGranularity), [currentGranularity, granularityTitle, granularityType, sectionId, widgetId]);
    const label = useMemo(() => getGranularityLabel(granularityTitle, granularityType), [granularityTitle, granularityType]);
    const showGranularityTooltip = label === granularity;
    return React.createElement("div", null,
        showGranularityTooltip
            ? React.createElement("div", { className: "insRadioGroupDiv bottom-margin" },
                React.createElement("span", { className: "insSettingsLabel" }, label),
                React.createElement(DataGranularityTooltipWidget, null))
            : null,
        React.createElement(AuxRadioGroup, { key: `${sectionId}-${widgetId}-${currentGranularity}`, data: data, label: showGranularityTooltip ? undefined : label, isStacked: true, onRadioGroupChanged: onRadioGroupChanged }));
};
